import { useEffect, useState } from 'react';
import { useFeatureFlagEnabled } from 'posthog-js/react';

// Enum of all feature flags used in the application
// https://us.posthog.com/project/40153/feature_flags?tab=overview

export const enum FeatureKeys {
  SOCIAL_LISTENING = 'social_listening',
  EMAIL_TEMPLATES = 'email_templates',
  CREATOR_DASHBOARD = 'creator_dashboard',
  ADVANCED_ANALYTICS = 'advanced_analytics',
  BULK_ACTIONS = 'bulk_actions',
}

// Enable features for non-prod environments if needed for testing
// Else on prod, features are enabled based on PostHog feature flag
const ENABLE_FOR_NON_PROD_ENV = import.meta.env.REACT_APP_SARAL_ENV !== 'prod';

/**
 * Custom hook for feature flag checking with persistence
 *
 * @param featureKey - The feature flag key to check
 * @returns boolean - Whether the feature is enabled
 */
const useFeature = (featureKey: FeatureKeys): boolean => {
  const [isEnabled, setIsEnabled] = useState<boolean>(false);
  const isPosthogEnabled = useFeatureFlagEnabled(featureKey);

  useEffect(() => {
    // Check if feature flag status is stored in localStorage
    const storedValue = localStorage.getItem(`feature_${featureKey}`);

    if (storedValue === 'true' || ENABLE_FOR_NON_PROD_ENV) {
      console.log(`Feature ${featureKey} is enabled for user from localStorage`);
      // If previously enabled, use that value
      setIsEnabled(true);
      return;
    }

    // For production, check PostHog feature flag
    if (isPosthogEnabled) {
      console.log(`Feature ${featureKey} is enabled for user from PostHog`);
      setIsEnabled(true);
      localStorage.setItem(`feature_${featureKey}`, 'true');
    } else {
      setIsEnabled(false);
    }
  }, [featureKey, isPosthogEnabled]);

  return isEnabled;
};

export default useFeature;
